// Shared helpers for the assessment and prequalification slices. Both slices
// resolve images/prequalification items that are linked to a parent entity
// (assessment or prequalification) and mutate them in place via Immer.

export const buildLinkedItemTargets = (linkedItems, relationKey) => {
  const targetsByParentId = new Map();

  for (const linkedItem of linkedItems || []) {
    if (!linkedItem?.id) {
      continue;
    }

    for (const parent of linkedItem[relationKey] || []) {
      if (!parent?.id) {
        continue;
      }

      let itemIds = targetsByParentId.get(parent.id);
      if (!itemIds) {
        itemIds = new Set();
        targetsByParentId.set(parent.id, itemIds);
      }
      itemIds.add(linkedItem.id);
    }
  }

  return targetsByParentId;
};

// Lazily resolves prequalification items by id. It scans the item array only as
// far as needed to answer each lookup (caching what it passes on the way), so a
// handler that touches one or two items doesn't pay to index every item — which
// matters when a parent has many prequalificationItems wrapped in Immer proxies.
export const createPrequalificationItemResolver = items => {
  const list = items || [];
  const cache = new Map();
  let scanned = 0;

  return {
    get(id) {
      if (id == null) {
        return undefined;
      }
      if (cache.has(id)) {
        return cache.get(id);
      }
      while (scanned < list.length) {
        const item = list[scanned++];
        const itemId = item?.id;
        if (itemId != null && !cache.has(itemId)) {
          cache.set(itemId, item);
        }
        if (itemId === id) {
          return item;
        }
      }
      return undefined;
    },
  };
};

export const forEachTargetState = (state, targets, callback) => {
  if (targets.size === 0) {
    return;
  }

  let remaining = targets.size;

  for (const currentState of state) {
    const itemIds = targets.get(currentState?.id);
    if (!itemIds) {
      continue;
    }

    callback(
      currentState,
      createPrequalificationItemResolver(currentState.prequalificationItem),
      itemIds,
    );

    if (--remaining === 0) {
      break;
    }
  }
};

export const stripImageRelations = image => {
  const { assessment, prequalification, ...strippedImage } = image;
  return strippedImage;
};
